import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowsClockwise, UploadSimple } from '@phosphor-icons/react';

const ST = { matched: 'bg-green-100 text-green-800', suggested: 'bg-amber-100 text-amber-800', unmatched: 'bg-slate-100 text-slate-600', ignored: 'bg-slate-100 text-slate-400 line-through' };
const fmt = (n) => Number(n || 0).toLocaleString();

export default function Reconciliation() {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [status, setStatus] = useState('all');
  const [manual, setManual] = useState({ date: new Date().toISOString().slice(0, 10), amount: '', reference: '', payer: '' });
  const { data: entries } = useQuery({ queryKey: ['recon'], queryFn: () => api.get('/recon/entries').then((r) => r.data) });
  const inval = () => { qc.invalidateQueries({ queryKey: ['recon'] }); qc.invalidateQueries({ queryKey: ['invoices'] }); };
  const upload = async (file) => {
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await api.post('/recon/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${data.imported} row(s) imported: ${data.auto_matched} auto-matched, ${data.suggested} suggested, ${data.unmatched} need review`);
      inval();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const addMut = useMutation({
    mutationFn: () => api.post('/recon/entries', { ...manual, amount: parseFloat(manual.amount) || 0 }),
    onSuccess: (r) => { toast.success(r.data.auto_matched ? 'Entry added and auto-matched to an invoice' : 'Entry added'); setManual({ ...manual, amount: '', reference: '', payer: '' }); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const autoMut = useMutation({
    mutationFn: () => api.post('/recon/auto-run'),
    onSuccess: (r) => { toast.success(`Auto-run: ${r.data.auto_matched} matched, ${r.data.suggested} suggested of ${r.data.processed}`); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const matchMut = useMutation({
    mutationFn: ({ id, invoice_id }) => api.post(`/recon/entries/${id}/match`, { invoice_id }),
    onSuccess: (r) => { toast.success(`Applied ${fmt(r.data.applied_amount)} to the invoice`); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const ignoreMut = useMutation({
    mutationFn: (id) => api.post(`/recon/entries/${id}/ignore`),
    onSuccess: () => inval(), onError: (e) => toast.error(errMsg(e)),
  });
  const manualPick = async (e) => {
    const q = window.prompt('Search open invoices by student name:'); if (q === null) return;
    try {
      const { data } = await api.get('/recon/open-invoices', { params: { q } });
      if (!data.length) { toast.warning('No open invoices found for that name'); return; }
      const pick = window.prompt(data.map((v, i) => `${i + 1}. ${v.student_name} - ${v.plan_name} (balance ${fmt(v.balance)})`).join('\n') + '\n\nEnter the number to apply this entry to:');
      const inv = data[parseInt(pick, 10) - 1];
      if (inv) matchMut.mutate({ id: e.id, invoice_id: inv.id });
    } catch (err) { toast.error(errMsg(err)); }
  };
  const filtered = (entries || []).filter((e) => status === 'all' || e.status === status);
  const counts = (entries || []).reduce((a, e) => ({ ...a, [e.status]: (a[e.status] || 0) + 1 }), {});
  return (
    <div className="space-y-5" data-testid="recon-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight flex items-center gap-2"><ArrowsClockwise size={26} className="text-orange-600" />Payment reconciliation</h1>
          <p className="text-sm text-slate-500">Import bank/UPI statements and let ONRID match payments to open invoices automatically. Razorpay payments reconcile themselves.</p>
        </div>
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={(e) => { if (e.target.files?.[0]) upload(e.target.files[0]); e.target.value = ''; }} />
          <Button data-testid="recon-upload-btn" variant="outline" onClick={() => fileRef.current?.click()}><UploadSimple size={15} className="me-1" />Upload bank CSV</Button>
          <Button data-testid="recon-auto-run" className="bg-slate-900 hover:bg-slate-800" disabled={autoMut.isPending} onClick={() => autoMut.mutate()}>Run auto-match</Button>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="recon-manual-form">
        <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">Add a single bank entry (CSV format: date, amount, reference, payer)</p>
        <div className="grid sm:grid-cols-5 gap-2">
          <Input data-testid="recon-date" type="date" value={manual.date} onChange={(e) => setManual({ ...manual, date: e.target.value })} />
          <Input data-testid="recon-amount" type="number" placeholder="Amount" value={manual.amount} onChange={(e) => setManual({ ...manual, amount: e.target.value })} />
          <Input data-testid="recon-reference" placeholder="Reference / narration" value={manual.reference} onChange={(e) => setManual({ ...manual, reference: e.target.value })} />
          <Input data-testid="recon-payer" placeholder="Payer (optional)" value={manual.payer} onChange={(e) => setManual({ ...manual, payer: e.target.value })} />
          <Button data-testid="recon-add-entry" disabled={!manual.amount || !manual.reference || addMut.isPending} onClick={() => addMut.mutate()} className="bg-orange-600 hover:bg-orange-500">Add & match</Button>
        </div>
      </div>
      <div className="flex gap-2 flex-wrap">
        {['all', 'unmatched', 'suggested', 'matched', 'ignored'].map((s) => (
          <button key={s} data-testid={`recon-filter-${s}`} onClick={() => setStatus(s)}
            className={`px-3 py-1.5 rounded-full text-xs ${status === s ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>
            {s} {s !== 'all' && counts[s] ? `(${counts[s]})` : ''}
          </button>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="recon-list">
        {filtered.length === 0 && <p className="text-sm text-slate-400">No entries here yet. Upload a bank statement CSV or add an entry above.</p>}
        {filtered.map((e) => (
          <div key={e.id} className="border-b border-slate-50 py-2.5 flex items-start justify-between gap-3 flex-wrap" data-testid={`recon-entry-${e.id}`}>
            <div className="text-sm">
              <b>{fmt(e.amount)}</b> <span className="text-xs text-slate-400">{e.date} · {e.source}</span>
              <span className={`ms-2 text-[11px] px-1.5 py-0.5 rounded ${ST[e.status]}`}>{e.status}</span>
              <p className="text-xs text-slate-500 mt-0.5">{e.reference}{e.payer ? ` · ${e.payer}` : ''}</p>
              {e.status === 'matched' && <p className="text-xs text-green-700">Applied {fmt(e.applied_amount)} to {e.matched_student} ({e.match_reason})</p>}
              {e.status === 'suggested' && e.suggestion && (
                <p className="text-xs text-amber-700">Suggested: {e.suggestion.student_name} - {e.suggestion.plan_name} (balance {fmt(e.suggestion.balance)}) · {e.suggestion.reason}</p>
              )}
            </div>
            {e.status !== 'matched' && e.status !== 'ignored' && (
              <div className="flex gap-2">
                {e.status === 'suggested' && e.suggestion && (
                  <button data-testid={`recon-accept-${e.id}`} onClick={() => matchMut.mutate({ id: e.id, invoice_id: e.suggestion.invoice_id })}
                    className="text-xs bg-green-700 text-white rounded px-2 py-1 hover:bg-green-600">Accept match</button>
                )}
                <button data-testid={`recon-pick-${e.id}`} onClick={() => manualPick(e)} className="text-xs border border-slate-200 rounded px-2 py-1 hover:bg-slate-50">Pick invoice</button>
                <button data-testid={`recon-ignore-${e.id}`} onClick={() => ignoreMut.mutate(e.id)} className="text-xs text-slate-400 hover:text-rose-600">Ignore</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
